import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getEventLocations,
  createEventLocation,
  updateEventLocation,
  deleteEventLocation,
} from '../api/event-locationAPI';

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    console.error('Failed to format date', error);
    return value;
  }
};

const sortLocations = (items) =>
  [...items].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, { sensitivity: 'base' }),
  );

const EventLocationLayer = () => {
  const userProfile = useUserProfile();
  const [locations, setLocations] = useState([]);
  const [formState, setFormState] = useState({ name: '' });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const resetDataTable = (fullyDestroy = false) => {
    if (dtRef.current) {
      dtRef.current.destroy(fullyDestroy);
      dtRef.current = null;
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await getEventLocations();
      setLocations(sortLocations(data));
    } catch (error) {
      console.error('Error loading event locations', error);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    resetDataTable();

    if (locations.length) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => {
      resetDataTable(true);
    };
  }, [locations]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const resetForm = () => {
    setFormState({ name: '' });
    setErrors({});
    setEditingId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (location) => {
    setFormState({ name: location.name || '' });
    setEditingId(location.id);
    setShowModal(true);
  };

  const validate = () => {
    const nextErrors = {};
    if (!formState.name?.trim()) {
      nextErrors.name = 'Name is required';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = { name: formState.name.trim() };
    if (editingId) {
      if (userProfile?.id) {
        payload.updatedBy = userProfile.id;
      }
    } else if (userProfile?.id) {
      payload.createdBy = userProfile.id;
    }

    try {
      let savedLocation;
      if (editingId) {
        savedLocation = await updateEventLocation(editingId, payload);
      } else {
        savedLocation = await createEventLocation(payload);
      }

      setShowModal(false);
      resetForm();
      resetDataTable();

      if (savedLocation) {
        setLocations((prev) => {
          if (editingId) {
            return sortLocations(prev.map((loc) => (loc.id === savedLocation.id ? savedLocation : loc)));
          }
          return sortLocations([...prev, savedLocation]);
        });
      } else {
        fetchLocations();
      }
    } catch (error) {
      console.error('Error saving event location', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event location?')) {
      return;
    }

    try {
      await deleteEventLocation(id);
      resetDataTable();
      setLocations((prev) => prev.filter((location) => location.id !== id));
    } catch (error) {
      console.error('Error deleting event location', error);
    }
  };

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {userProfile?.privileges?.includes('events:create') && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Event Location
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm'>
          <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Created At</th>
                <th>Created By</th>
                <th>Updated At</th>
                <th>Updated By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-4 rounded-0">No Locations Setup</td>
                </tr>
              )}
              {locations.map((location) => (
                <tr key={location.id}>
                  <td><strong>{location.name}</strong></td>
                  <td>{formatDateTime(location.createdAt)}</td>
                  <td>{location.createdBy ?? ''}</td>
                  <td>{formatDateTime(location.updatedAt)}</td>
                  <td>{location.updatedBy ?? ''}</td>
                  <td>
                    <div className='d-flex gap-2'>
                      {userProfile?.privileges?.includes('events:update') && (
                        <button
                          className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleEdit(location)}
                        >
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('events:delete') && (
                        <button
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleDelete(location.id)}
                        >
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Event Location' : 'New Event Location'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Name *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name='name'
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Close
                </button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventLocationLayer;
